const HEADER_PATTERNS = {
  // ... (All your existing HEADER_PATTERNS are correct and remain here)
  Symbol: /symbol|ticker|instrument/i,
  Quantity: /quantity|qty|shares/i,
  'Date Acquired': /acquired|purchase date|date acquired|open date/i,
  'Date Sold': /sold|sale date|date sold|close date/i,
  'Cost Basis': /cost|cost basis|total cost/i,
  Proceeds: /proceeds|sale total|total proceeds/i,
  Employer: /employer|company|payer/i,
  Wages: /wages|income|box 1|gross pay/i,
  'Federal Tax Withheld': /federal|fed tax|box 2/i,
  Date: /date|trans date|transaction date|posting date/i,
  Description: /description|memo|details|transaction/i,
  Amount: /amount|total/i,
  'Property Description': /property|description|address/i,
  'Rents Received': /rent|rents received|income/i,
  Advertising: /advertising|ads/i,
  Repairs: /repairs|maintenance/i,
  Insurance: /insurance/i,
  'Property Taxes': /tax|property tax|taxes/i,
  'Asset Description': /asset|item|description/i,
  'Useful Life': /life|useful life|term/i,
};

const CONTENT_PATTERNS = {
  // Matches YYYY-MM-DD, MM/DD/YYYY, M/D/YY etc.
  DATE: /^(?:(?:1[0-2]|0?[1-9])\/(?:3[01]|[12][0-9]|0?[1-9])\/(?:\d{2}|\d{4})|\d{4}-(?:1[0-2]|0[1-9])-(?:3[01]|[12][0-9]|0[1-9]))$/,
  
  // Matches strings with letters, numbers, spaces, and common description characters.
  DESCRIPTION: /^[A-Za-z0-9\s#\-\/&'.]+$/, // Added apostrophe for names like "O'Malley's"
  
  // Matches currency values like $1,234.56 | 1234.56 | -1,234 | ($1234.00)
  AMOUNT: /^\(?\$?\s*-?[\d,]+(?:\.\d{2})?\)?$/,
};

/**
 * A helper function to find the first unmapped column that matches a content regex.
 * @param {string[]} headers - All file headers.
 * @param {Object[]} dataSample - Sample of the data.
 * @param {Set<string>} usedHeaders - Headers that are already mapped.
 * @param {RegExp} contentRegex - The regex to test cell content against.
 * @returns {string|null} The header name if a match is found, otherwise null.
 */
function findFirstColumnByContent(headers, dataSample, usedHeaders, contentRegex) {
  // Iterate through headers in their original order (left to right).
  for (const header of headers) {
    if (usedHeaders.has(header)) continue; // Skip if already mapped.

    // Check if at least 80% of the sample data in this column matches the content pattern.
    // This handles cases where a few cells might be blank or have notes.
    const nonEmptyRows = dataSample.filter(row => row[header]);
    if (nonEmptyRows.length === 0) continue; // Skip empty columns

    const matchCount = nonEmptyRows.filter(row => contentRegex.test(row[header])).length;
    const matchPercentage = matchCount / nonEmptyRows.length;

    if (matchPercentage >= 0.8) {
      return header; // Found the first matching column.
    }
  }
  return null; // No column matched.
}


/**
 * Analyzes a file to automatically create a mapping.
 * @param {string[]} fileHeaders - The column headers from the user's CSV file.
 * @param {Object[]} dataSample - A sample of the first few rows of data.
 * @param {string[]} requiredAppFields - An array of the fields our app needs.
 * @returns {Object|null} A mapping object if successful, or null if it fails validation.
 */
export function autoMapByRegex(fileHeaders, dataSample, requiredAppFields) {
  const mapping = {};
  const usedFileHeaders = new Set();

  // --- Phase 1: Match by Header Name (High Confidence) ---
  for (const appField of requiredAppFields) {
    const pattern = HEADER_PATTERNS[appField];
    if (!pattern) continue;

    for (const fileHeader of fileHeaders) {
      if (pattern.test(fileHeader) && !usedFileHeaders.has(fileHeader)) {
        mapping[appField] = fileHeader;
        usedFileHeaders.add(fileHeader);
        break;
      }
    }
  }

  // --- Phase 2: Content-Aware Matching for Missed Fields ---
  
  // If 'Description' was missed, find the first text-like column.
  const descriptionField = requiredAppFields.find(f => f.includes('Description'));
  if (descriptionField && !mapping[descriptionField]) {
    const foundHeader = findFirstColumnByContent(fileHeaders, dataSample, usedFileHeaders, CONTENT_PATTERNS.DESCRIPTION);
    if (foundHeader) {
      mapping[descriptionField] = foundHeader;
      usedFileHeaders.add(foundHeader);
    }
  }

  // If 'Date' was missed, find the first date-like column.
  if (requiredAppFields.includes('Date') && !mapping['Date']) {
    const foundHeader = findFirstColumnByContent(fileHeaders, dataSample, usedFileHeaders, CONTENT_PATTERNS.DATE);
    if (foundHeader) {
      mapping['Date'] = foundHeader;
      usedFileHeaders.add(foundHeader);
    }
  }

  // If 'Amount' was missed, find the first amount-like column.
  const amountField = requiredAppFields.find(f => ['Amount', 'Cost Basis', 'Proceeds', 'Wages'].includes(f));
  if (amountField && !mapping[amountField]) {
    const foundHeader = findFirstColumnByContent(fileHeaders, dataSample, usedFileHeaders, CONTENT_PATTERNS.AMOUNT);
    if (foundHeader) {
      mapping[amountField] = foundHeader;
      usedFileHeaders.add(foundHeader);
    }
  }

  // --- Phase 3: Final Validation (Unchanged) ---
  const criticalFields = [
    'Cost Basis', 'Proceeds', 'Wages', 'Amount', 'Federal Tax Withheld',
    'Rents Received', 'Cost' 
  ];
  const requiredCriticalFields = requiredAppFields.filter(field => criticalFields.includes(field));
  const allCriticalFieldsFound = requiredCriticalFields.every(field => mapping[field]);

  if (allCriticalFieldsFound) {
    console.log('SUCCESS: Auto-mapping found critical fields:', mapping);
    return mapping;
  } else {
    console.log('FAILURE: A critical dollar amount field was not found.');
    return null;
  }
}
